import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Theme } from "@astryxdesign/core/theme";
import { neutralTheme } from "@astryxdesign/theme-neutral/built";
import { z } from "zod";

const appearanceSchema = z.enum(["system", "light", "dark"]);
type Appearance = z.infer<typeof appearanceSchema>;
const storageKey = "ssbd-access-appearance";
function readAppearance(): Appearance {
  try {
    return appearanceSchema.catch("system").parse(localStorage.getItem(storageKey));
  } catch {
    return "system";
  }
}
const AppearanceContext = createContext<{
  mode: Appearance;
  setMode: (mode: Appearance) => void;
} | null>(null);
export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [mode, updateMode] = useState(readAppearance);
  useEffect(() => {
    const changed = (event: StorageEvent) => {
      if (event.key === storageKey || event.key === null) updateMode(readAppearance());
    };
    window.addEventListener("storage", changed);
    return () => window.removeEventListener("storage", changed);
  }, []);
  function setMode(value: Appearance) {
    updateMode(value);
    try {
      localStorage.setItem(storageKey, value);
    } catch {
      /* Appearance still works when storage is unavailable. */
    }
  }
  return (
    <AppearanceContext.Provider value={{ mode, setMode }}>
      <Theme theme={neutralTheme} mode={mode}>
        {children}
      </Theme>
    </AppearanceContext.Provider>
  );
}
export function AppearanceSelect() {
  const appearance = useContext(AppearanceContext);
  if (!appearance) throw new Error("AppearanceProvider is missing");
  return (
    <label className="appearance-control">
      <span>Appearance</span>
      <select
        aria-label="Appearance"
        value={appearance.mode}
        onChange={(event) => appearance.setMode(appearanceSchema.parse(event.target.value))}
      >
        <option value="system">System</option>
        <option value="light">Light</option>
        <option value="dark">Dark</option>
      </select>
    </label>
  );
}
